import dayjs from "dayjs";
import ReactDOMServer from "react-dom/server";
import styled from "styled-components";

import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes, setFont } from "@similarweb/styles";

import { CenteredFlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";

export const algoChangeDateConfig =
    (algoChangeDateUTC: number, options: FixmeType) =>
    ({ data }: FixmeType) => {
        const { showTooltip, description, iconName, MarkerComponent, showDateTitle } = Object.assign(
            {
                showTooltip: false,
                iconName: "alerts",
                MarkerComponent: Marker,
                showDateTitle: true,
                overwriteDateRange: false,
                isPointArray: true, // case true: data[0][0] , case false: data[0].x
            },
            options,
        );

        const setDateRange = () => {
            const { overwriteDateRange = false, isPointArray = true } = options;

            if (!overwriteDateRange) {
                return data.reduce((acc: FixmeType, { data: points }: FixmeType) => {
                    const datesArr = points.map((p: FixmeType) => p.x);
                    return [Math.min(acc[0] ?? datesArr[0], ...datesArr), Math.max(acc[1] ?? datesArr[0], ...datesArr)];
                }, []);
            } else {
                const datesArr = data.map((p: FixmeType) => (isPointArray ? p[0] : p.x));
                return [Math.min(...datesArr), Math.max(...datesArr)];
            }
        };

        const datesRange = setDateRange();
        const dateAvg = (datesRange[1] + datesRange[0]) / 2;
        const tooltipSide = algoChangeDateUTC < dateAvg ? "right" : "left";
        return {
            xAxis: {
                plotLines: [
                    {
                        color: "#7F8B97",
                        width: 1,
                        dashStyle: "Dash",
                        value: algoChangeDateUTC,
                        left: 11,
                        zIndex: 1,
                        label: {
                            text: ReactDOMServer.renderToString(
                                <MarkerComponent>
                                    <MarkerIcon iconName={iconName} />
                                    {showTooltip ? (
                                        <MarkerTooltip side={tooltipSide}>
                                            {showDateTitle ? (
                                                <MarkerTooltipTitle>
                                                    {dayjs(algoChangeDateUTC).format("dddd, MMM DD, YYYY")}
                                                </MarkerTooltipTitle>
                                            ) : null}
                                            <MarkerTooltipDescription>{description}</MarkerTooltipDescription>
                                        </MarkerTooltip>
                                    ) : null}
                                </MarkerComponent>,
                            ),
                            useHTML: true,
                            x: -10,
                            y: 0,
                            rotation: 0,
                            verticalAlign: "bottom",
                        },
                    },
                ],
            },
        };
    };

const Marker = styled(CenteredFlexRow).attrs((props) => ({
    className: "PlainTooltip-container",
}))`
    top: 2px;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background-color: ${colorsPalettes.blue[400]};
`;

const MarkerIcon = styled(SWReactIcons)`
    path {
        fill: white;
    }

    height: 12px;
    width: 12px;
`;

const MarkerTooltip = styled.div.attrs((props) => ({
    className: "PlainTooltip-element",
}))<{ side: string }>`
    width: 200px;
    padding: 12px;
    opacity: 0.9;
    bottom: 20px;
    display: none;

    &:before,
    &:after {
        display: none;
    }

    ${({ side }) => {
        switch (side) {
            case "left":
                return `right: 20px;`;
            case "right":
                return `left: 20px;`;
        }
    }}
`;

const MarkerTooltipTitle = styled.div`
    ${setFont({ $size: "16px", $weight: "600", $color: colorsPalettes.carbon[50] })};
    margin-bottom: 10px;
`;

const MarkerTooltipDescription = styled.div`
    overflow-wrap: normal;
    white-space: normal;
    line-height: 20px;
    ${setFont({ $size: "14px", $weight: "300", $color: colorsPalettes.carbon[50] })};
`;
